import type { SanitySocialProofRatingStars } from '#/lib/sanity/queries/socialProof';
import { cn, COLORS } from '#/lib/utils';

import { Image } from '../Image';
import { RichText } from '../RichText';

const STAR_COUNT = 5;
const UNFILLED_STAR_COLOR = '#D1D5DB';

export function RatingStarsSocialProof({
  rating,
  logo,
  text,
}: SanitySocialProofRatingStars & { className?: string }) {
  return (
    <div className="flex items-center gap-12">
      <Image image={logo} className="w-32 min-w-32" sizes="100px" />
      <div className="flex flex-col gap-4">
        <RatingStars rating={rating} />
        <span className="text-12 font-[400] text-charcoal leading-100">
          {rating}/5 Stars
        </span>
      </div>
      <div className="pl-20 max-w-220">
        <RichText
          value={text}
          normalTextSizeClassName="text-12 leading-120 tracking-[-0.12px]"
        />
      </div>
    </div>
  );
}

interface StarProps {
  fill: string;
  gradientId?: string;
  partialFillPercentage?: `${number}%`;
}

function RatingStars({ rating }: { rating: number }) {
  const filledStars = Math.floor(rating);
  const finalStarFill = rating - filledStars;
  return (
    <div className={cn('flex items-center gap-4')}>
      {Array.from({ length: STAR_COUNT }).map((_, index) => {
        // Filled Stars
        if (index < filledStars)
          return <Star key={index} fill={COLORS.charcoal} />;

        // Partially Filled Star
        if (index === filledStars && finalStarFill > 0)
          return (
            <Star
              key={index}
              fill={`url(#star-partial)`}
              gradientId={`star-partial`}
              partialFillPercentage={`${finalStarFill * 100}%`}
            />
          );

        // Empty Star
        return <Star key={index} fill={UNFILLED_STAR_COLOR} />;
      })}
    </div>
  );
}
function Star({ fill, gradientId, partialFillPercentage }: StarProps) {
  return (
    <span className="inline-block">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {partialFillPercentage && gradientId ? (
          <defs>
            <linearGradient
              id={gradientId}
              x1="0"
              y1="0"
              x2="14"
              y2="0"
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset={partialFillPercentage}
                stopColor={COLORS.charcoal}
              />
              <stop
                offset={partialFillPercentage}
                stopColor={UNFILLED_STAR_COLOR}
              />
              <stop offset="100%" stopColor={UNFILLED_STAR_COLOR} />
            </linearGradient>
          </defs>
        ) : null}
        <path
          d="M9.22168 4.48828L13.7646 5.46582L10.668 8.90723L11.1357 13.5L6.88281 11.6387L2.62891 13.5L3.09668 8.90723L0 5.46582L4.54297 4.48828L6.88281 0.5L9.22168 4.48828Z"
          fill={fill}
        />
      </svg>
    </span>
  );
}
